import React, { useEffect, useRef, useState } from "react"
import { Box, Button, Flex, Text } from "@chakra-ui/react"

import { useCart } from "@app/hooks/useCart"
import { useCore } from "@app/hooks/useCore"
import { useAppContext } from "@app/providers/app"
import { useShopifyPrice } from "@app/hooks/useShopify"
import { useOutOfStock } from "@app/hooks/useOutOfStock"

import { useBundle } from "@app/hooks/useBundle"
import { AnimatePresence } from "framer-motion"
import MotionBox from "@app/components/MotionBox"

type Props = {
  product: any
  quantity: number
  loading: boolean
  handleBackInStock: () => void
  productAddBottomRef: React.RefObject<HTMLDivElement>
}

const ProductFloatingAddToCart: React.FC<Props> = ({ product, quantity, loading, handleBackInStock, productAddBottomRef }) => {
  const floatingCartRef = useRef<HTMLDivElement>(null)
  const { addToCart, loading: cartLoading } = useCart()
  const { addBundleToCart, bundlePriceDiscounted, bundlePriceFull, isOnSale, isBundle } = useBundle(product)
  const {
    helpers: { isBrowser },
  } = useCore()
  const { activeVariant } = useAppContext()
  const { formattedPrice, formattedCompareAtPrice, onSale } = useShopifyPrice(activeVariant, quantity)
  const { stockStatus, isNotifyFlow, isDisabledFlow } = useOutOfStock(product)
  const [visible, setVisible] = useState(false)

  const onClick = () => {
    if (activeVariant?.availableForSale && !loading && !cartLoading) {
      addToCart(product, activeVariant, quantity)
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      if (floatingCartRef.current && productAddBottomRef.current) {
        const cartRect = floatingCartRef.current.getBoundingClientRect()
        const footerRect = productAddBottomRef.current.getBoundingClientRect()

        if (-cartRect.bottom > footerRect.top) {
          setVisible(false)
        } else if (cartRect?.top < 145) {
          setVisible(true)
        } else if (cartRect?.top > 145) {
          setVisible(false)
        }
      }
    }
    if (isBrowser) {
      window.addEventListener("scroll", handleScroll)
    }
    return () => {
      if (isBrowser) {
        window.removeEventListener("scroll", handleScroll)
      }
    }
  }, [isBrowser, productAddBottomRef])

  return (
    <Box ref={floatingCartRef} pointerEvents={visible ? "auto" : "none"}>
      <AnimatePresence>
        {!isDisabledFlow && visible && !loading && product?.published && activeVariant?.availableForSale && !product?.inStoreOnly ? (
          <MotionBox
            pos="fixed"
            left={0}
            w="100%"
            zIndex={750}
            transition={{ duration: 0.4 }}
            initial={{ bottom: -10, opacity: 0 }}
            animate={{ bottom: 0, opacity: 1 }}
            exit={{ bottom: -10, opacity: 0 }}
          >
            <Box mx="auto" p="20px" maxW="700px" background="primary.white" borderTopRadius="32px">
              {isBundle ? (
                <Button
                  variant="fillRainbowLoader"
                  border="none"
                  justifyContent="center"
                  w="100%"
                  isDisabled={cartLoading}
                  onClick={() => addBundleToCart(quantity)}
                  transition="none"
                >
                  {stockStatus ? (
                    stockStatus
                  ) : (
                    <>
                      <Text variant="text18" display={{ base: "none", sm: "block" }} mr="10px">
                        {product?.title}
                      </Text>
                      <Box>
                        <Flex justifyContent="space-between" w="100%" alignItems="center">
                          <Text variant="text18">{cartLoading ? "Adding" : "Add"}</Text>
                          <Flex ml="10px" display="inline-flex">
                            {isOnSale ? (
                              <>
                                <Text variant="text18" pr="5px" textDecoration="line-through">
                                  {bundlePriceFull}
                                </Text>
                                <Text variant="text18">{bundlePriceDiscounted}</Text>
                              </>
                            ) : (
                              <Text variant="text18">{bundlePriceFull}</Text>
                            )}
                          </Flex>
                        </Flex>
                      </Box>
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  variant={!isNotifyFlow && activeVariant ? "fillRainbowLoader" : "fill"}
                  border="none"
                  justifyContent="center"
                  w="100%"
                  isDisabled={cartLoading}
                  onClick={isNotifyFlow ? handleBackInStock : onClick}
                  transition="none"
                >
                  {stockStatus ? (
                    stockStatus
                  ) : (
                    <>
                      <Text variant="text18" display={{ base: "none", sm: "block" }} mr="10px">
                        {product?.title}
                      </Text>
                      <Box>
                        <Flex justifyContent="space-between" w="100%" alignItems="center">
                          <Text variant="text18">{cartLoading ? "Adding" : "Add"}</Text>
                          <Flex ml="10px" display="inline-flex">
                            {onSale && (
                              <Text variant="text18" pr="5px" textDecoration="line-through">
                                {formattedCompareAtPrice}
                              </Text>
                            )}
                            <Text variant="text18">{formattedPrice}</Text>
                          </Flex>
                        </Flex>
                      </Box>
                    </>
                  )}
                </Button>
              )}
            </Box>
          </MotionBox>
        ) : null}
      </AnimatePresence>
    </Box>
  )
}

export default React.memo(ProductFloatingAddToCart)
